<template>
    <div
        ref="rootEl"
        class="main-layout"
        :class="{
            'sidebar-hidden': !sidebarExpanded,
            'sidebar-collapsed': sidebarCollapsed,
            'dark-theme': isDarkTheme,
            'light-theme': !isDarkTheme,
        }"
    >
        <a href="#" @click="skipMainContent" class="skip-main-content">
            <span>{{ t("Skip to main content") }}</span>
        </a>
        <TopBar @toggle-menu="toggleMenu" @openModal="openModal"></TopBar>
        <MenuSideBar v-model:expanded="sidebarExpanded" v-model:collapsed="sidebarCollapsed" @openModal="openModal"></MenuSideBar>
        <div v-if="sidebarExpanded" class="side-bar-overlay" @click="closeSideBar"></div>
        <AuthLoadingOverlay :display="loadingAuth"></AuthLoadingOverlay>
        <router-view v-if="!loadingAuth"></router-view>
        <LoadingOverlay v-if="loadingRoute"></LoadingOverlay>

        <AccountSettingsDropdown
            v-if="displayDropdownUserSettings"
            v-model:display="displayDropdownUserSettings"
            @openModal="openModal"
        ></AccountSettingsDropdown>

        <NotificationsDropdown v-if="displayDropdownNotifications" v-model:display="displayDropdownNotifications"></NotificationsDropdown>

        <ChangeLanguageDropdown
            v-if="displayDropdownLanguage"
            v-model:display="displayDropdownLanguage"
            @openModal="openModal"
        ></ChangeLanguageDropdown>

        <ChangeLanguageModal v-if="displayModalLanguage" v-model:display="displayModalLanguage"></ChangeLanguageModal>

        <ChangeThemeDropdown
            v-if="displayDropdownTheme"
            v-model:display="displayDropdownTheme"
            @openModal="openModal"
        ></ChangeThemeDropdown>

        <ProfileModal v-if="displayProfileModal" v-model:display="displayProfileModal" :uid="profileModalUser"></ProfileModal>

        <LogoutModal v-if="displayModalLogout" v-model:display="displayModalLogout"></LogoutModal>

        <MessageModal
            v-if="displayModalMessage"
            v-model:display="displayModalMessage"
            :title="messageModalTitle"
            :message="messageModalMessage"
            :container-class="monitoringModalClass"
        ></MessageModal>

        <ConfirmationModal
            v-if="displayConfirmationModal"
            v-model:display="displayConfirmationModal"
            :title="confirmationTitle"
            :message="confirmationMessage"
            :danger="confirmationDanger"
            :container-class="monitoringModalClass"
            @confirm="onConfirm"
        >
        </ConfirmationModal>

        <CookiesModal v-if="displayModalCookies" v-model:display="displayModalCookies"></CookiesModal>

        <SnackBar></SnackBar>
    </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from "vue";
import { useRoute } from "vue-router";

import TopBar from "@/components/layout/TopBar.vue";
import MenuSideBar from "@/components/layout/MenuSideBar.vue";
import LoadingOverlay from "@/components/layout/LoadingOverlay.vue";
import AuthLoadingOverlay from "@/components/layout/AuthLoadingOverlay.vue";
import SnackBar from "@/components/layout/SnackBar.vue";

import LogoutModal from "@/components/modals/auth/LogoutModal.vue";

import AccountSettingsDropdown from "@/components/dropdowns/account/AccountSettingsDropdown.vue";
import NotificationsDropdown from "@/components/dropdowns/account/NotificationsDropdown.vue";

import ChangeLanguageDropdown from "@/components/dropdowns/account/ChangeLanguageDropdown.vue";
import ChangeThemeDropdown from "@/components/dropdowns/account/ChangeThemeDropdown.vue";

import ChangeLanguageModal from "@/components/modals/ChangeLanguageModal.vue";

import ProfileModal from "@/components/modals/ProfileModal.vue";

import MessageModal from "@/components/modals/MessageModal.vue";

import ConfirmationModal from "@/components/modals/ConfirmationModal.vue";

import CookiesModal from "@/components/modals/CookiesModal.vue";

import { AuthController } from "@/control/auth";
import { NotificationsController } from "@/control/notifications";
import { getCookiePreference, getSidebarCollapsed, getTheme, setSidebarCollapsed } from "@/control/app-preferences";
import { useAppEventListener } from "@/composables/useAppEventListener";

defineOptions({ name: "MainLayout" });

const route = useRoute();
const rootEl = useTemplateRef<HTMLElement>("rootEl");

const loadingRoute = ref(false);

const DESKTOP_BREAKPOINT = 1000;

const sidebarExpanded = ref(window.innerWidth >= DESKTOP_BREAKPOINT);
const sidebarCollapsed = ref(getSidebarCollapsed());

const isDarkTheme = ref(false);
const loadingAuth = ref(AuthController.Loading && !AuthController.FirstTimeLoaded);

const displayModalLogout = ref(false);
const displayDropdownUserSettings = ref(false);
const displayDropdownNotifications = ref(false);

const displayDropdownTheme = ref(false);

const displayDropdownLanguage = ref(false);

const displayModalLanguage = ref(false);

const displayModalMessage = ref(false);
const messageModalTitle = ref("");
const messageModalMessage = ref("");

const displayConfirmationModal = ref(false);
const confirmationTitle = ref("");
const confirmationMessage = ref("");
const confirmationDanger = ref(false);

const displayProfileModal = ref(false);
const profileModalUser = ref("");

const displayModalCookies = ref(false);

const monitoringModalClass = computed(() => {
    const name = String(route.name || "");
    return name === "home" || name === "project-detail" || name === "check-detail" ? "monitoring-modal" : "";
});

let confirmationCallback: (() => void) | null = null;
let resizeObserver: ResizeObserver | null = null;

const onAuthStatusUpdate = () => {
    displayDropdownUserSettings.value = false;
    displayDropdownNotifications.value = false;

    displayModalLogout.value = false;

    displayDropdownLanguage.value = false;
    displayModalLanguage.value = false;

    displayDropdownTheme.value = false;
};

const closeSideBar = () => {
    sidebarExpanded.value = false;
};

const onAuthLoadingChanged = () => {
    loadingAuth.value = AuthController.Loading && !AuthController.FirstTimeLoaded;
};

const onThemeChanged = () => {
    isDarkTheme.value = getTheme() === "dark";
};

const toggleMenu = () => {
    if (window.innerWidth >= DESKTOP_BREAKPOINT) {
        // On desktop the side bar is always docked, so the menu button
        // toggles between the collapsed (icons only) and expanded state.
        sidebarCollapsed.value = !sidebarCollapsed.value;
    } else {
        // On mobile it toggles the overlay visibility.
        sidebarExpanded.value = !sidebarExpanded.value;
    }
};

const skipMainContent = (event?: Event) => {
    if (event) {
        event.preventDefault();
    }
    const content = document.querySelector(".page-content") as HTMLElement | null;
    if (content) {
        content.focus();
    }
};

const showMessage = (title: string, msg: string) => {
    messageModalTitle.value = title;
    messageModalMessage.value = msg;
    displayModalMessage.value = true;
};

const openModal = (name: string) => {
    switch (name) {
        case "user-settings":
            displayDropdownNotifications.value = false;
            displayDropdownUserSettings.value = !displayDropdownUserSettings.value;
            break;
        case "notifications":
            displayDropdownUserSettings.value = false;
            displayDropdownNotifications.value = !displayDropdownNotifications.value;
            break;
        case "change-language":
            displayDropdownLanguage.value = true;
            break;
        case "change-language-modal":
            displayModalLanguage.value = true;
            break;
        case "change-theme":
            displayDropdownTheme.value = true;
            break;
        case "logout":
            displayModalLogout.value = true;
            break;
    }
};

const onRouterLoading = (loading: boolean) => {
    loadingRoute.value = loading;
};

const onRouteChanged = () => {
    // The side bar is always docked on desktop and can be collapsed with its
    // toggle button. On mobile it behaves as an overlay that closes after
    // navigating to a new page.
    sidebarExpanded.value = window.innerWidth >= DESKTOP_BREAKPOINT;

    const cookiePref = getCookiePreference();

    if (!cookiePref && (!route.name || !(route.name in { terms: 1, privacy: 1, cookies: 1 }))) {
        displayModalCookies.value = true;
    } else {
        displayModalCookies.value = false;
    }
};

const showConfirmationModal = (options: { title: string; message: string; danger?: boolean; callback: () => void }) => {
    displayConfirmationModal.value = true;
    confirmationTitle.value = options.title;
    confirmationMessage.value = options.message;
    confirmationDanger.value = !!options.danger;
    confirmationCallback = options.callback;
};

const onConfirm = () => {
    if (confirmationCallback) {
        confirmationCallback();
    }
};

const onShowProfile = (uid: string) => {
    profileModalUser.value = uid;
    displayProfileModal.value = true;
};

const onPageResize = () => {
    sidebarExpanded.value = window.innerWidth >= DESKTOP_BREAKPOINT;
};

useAppEventListener("auth-status-changed", onAuthStatusUpdate);
useAppEventListener("auth-status-loading", onAuthLoadingChanged);
useAppEventListener("theme-changed", onThemeChanged);
useAppEventListener("msg-modal", showMessage);
useAppEventListener("ask-confirmation", showConfirmationModal);
useAppEventListener("show-profile", onShowProfile);
useAppEventListener("router-load-state-change", onRouterLoading);

watch(() => route.fullPath, onRouteChanged);

watch(sidebarCollapsed, (value) => {
    setSidebarCollapsed(value);
});

onMounted(() => {
    onThemeChanged();
    onAuthStatusUpdate();
    onRouteChanged();
    NotificationsController.emitChanged();

    if (window.ResizeObserver && rootEl.value) {
        resizeObserver = new ResizeObserver(onPageResize);
        resizeObserver.observe(rootEl.value);
    }
});

onBeforeUnmount(() => {
    if (resizeObserver) {
        resizeObserver.disconnect();
    }
});
</script>

<style></style>
